import Vue from 'vue';
import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import values from 'lodash/values';
import { createFrozenMap } from '@/vuex/utils';
import {
    fetchLayouts,
    fetchOrganizationLayouts,
    deleteLayout,
    fetchLayoutCategories,
    sortByName,
    createUnassignedMetaGroup,
} from './layouts.utils';

import { localizeDefaultGuideCategories } from '../guide-categories/guide-categories.utils';
import { isWebBasedApp, isMobileApp, isEmailPlatform } from '@/utils/apps';

const layouts = {
    namespaced: true,
    state: {
        map: {},
        categoryMap: {},
    },
    mutations: {
        setLayouts (state, { layouts }) {
            state.map = keyBy(layouts, 'id');
        },
        setLayout (state, { layout }) {
            state.map = {
                ...state.map,
                [layout.id]: layout,
            };
        },
        removeLayoutFromMap (state, { id }) {
            Vue.delete(state.map, id);
        },
        setLayoutCategories (state, { categories }) {
            state.categoryMap = categories;
        },
    },
    actions: {
        async loadAll ({ rootGetters, state, commit }, { reFetch = false } = {}) {
            if (Object.keys(state.map).length && !reFetch) {
                return;
            }

            const activeOrganization = rootGetters['organization/getActive'];
            const orgId = get(activeOrganization, 'id', null);
            const activeSubscription = rootGetters['subscription/getActiveSub'];
            const subId = get(activeSubscription, 'id', null);
            const includeOrganizationResources = get(
                activeOrganization,
                'organizationFlags.enableResourceSharing',
                false,
            );

            const requests = [fetchLayouts()];
            if (orgId && includeOrganizationResources) {
                requests.push(fetchOrganizationLayouts(orgId, subId));
            }
            const promises = await Promise.all(requests);
            const layouts = promises.reduce((prevPromise, currPromise) => prevPromise.concat(currPromise));

            commit('setLayouts', { layouts });
        },
        async delete ({ commit }, { layout }) {
            await deleteLayout(layout.id);

            commit('removeLayoutFromMap', { id: layout.id });
        },
        async loadCategories ({ commit }, { items = false } = {}) {
            try {
                const { data: categories } = await fetchLayoutCategories({ items });
                // Default layout categories match default guide categories
                // Using the localization function designed for guide categories so that localized text also matches
                const localizedCategories = localizeDefaultGuideCategories(categories);
                commit('setLayoutCategories', {
                    categories: createFrozenMap(localizedCategories),
                });
            } catch (error) {
                throw error;
            }
        },
    },
    getters: {
        list: (state) => values(state.map),
        listForPlatform: (state, getters) => (platform, excludeMultiStep = false) => {
            const isMobile = isMobileApp(platform);
            const isWebApp = isWebBasedApp(platform);
            const isEmail = isEmailPlatform(platform);

            return getters.list.filter(({ supportedPlatforms, steps }) => {
                if (excludeMultiStep && steps?.length > 1) return false;

                if (!supportedPlatforms) {
                    return isWebApp;
                }

                if (isWebApp && !supportedPlatforms.web) {
                    return false;
                }

                if (isMobile && !supportedPlatforms.mobile) {
                    return false;
                }

                if (isEmail && !supportedPlatforms.web && !supportedPlatforms.email) {
                    return false;
                }

                return true;
            });
        },
        layoutCategories: (state) => values(state.categoryMap),
        layoutCategoriesGroupedByMetaGroup: (state, getters, rootState, rootGetters) => (platform) => {
            const shouldIncludeCategory = (category) => {
                if (!category) return false;
                if (category.attributes?.platform !== platform) return false;

                return true;
            };
            const groupedCategoryIds = new Set();

            const metaGroupsWithCategories = sortByName(rootGetters['metaGroups/list']).map((metaGroup) => {
                const items = metaGroup.items
                    .map((item) => {
                        groupedCategoryIds.add(item.itemId);

                        return state.categoryMap[item.itemId];
                    })
                    .filter(shouldIncludeCategory);

                return { ...metaGroup, items: sortByName(items) };
            });

            const unassignedCategories = getters.layoutCategories.filter((category) =>
                !groupedCategoryIds.has(category.id) && shouldIncludeCategory(category));
            const unassignedMetaGroup = createUnassignedMetaGroup(unassignedCategories);

            metaGroupsWithCategories.push(unassignedMetaGroup);

            return metaGroupsWithCategories;
        },
    },
};

export default layouts;
